<!-- @catalog Overlapping row of avatars with a "+N" chip for the rest; for a single person use CAvatar. -->
<template>
  <div class="c-avatar-group">
    <c-avatar
      v-for="person in visible"
      :key="person.id"
      class="c-avatar-group__item"
      :name="person.name"
      :src="person.src"
      :size="size"
      :font-size="fontSize"
      color="blue"
    />

    <span v-if="remaining > 0" class="c-avatar-group__more" :style="moreStyle">
      +{{ remaining }}
    </span>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";

export type CAvatarGroupPerson = {
  id: number | string;
  name: string;
  src?: string | null;
};

const props = withDefaults(
  defineProps<{
    people: CAvatarGroupPerson[];
    // How many people the group actually stands for. Pass it when `people` is a
    // capped preview, so the "+N" counts everybody and not just what was sent.
    total?: number | null;
    size?: string;
    fontSize?: string;
  }>(),
  {
    total: null,
    size: "32px",
    fontSize: "10px",
  },
);

const visible = computed(() => props.people);

const remaining = computed(() =>
  Math.max(0, (props.total ?? props.people.length) - props.people.length),
);

// The chip is a circle of the same size, so it lines up with the avatars.
const moreStyle = computed(() => ({
  width: props.size,
  height: props.size,
  fontSize: props.fontSize,
}));
</script>

<style scoped lang="scss">
.c-avatar-group {
  @apply tw-flex tw-items-center;

  &__item,
  &__more {
    @apply tw-shrink-0 tw-ring-2 tw-ring-white;

    &:not(:first-child) {
      margin-left: -8px;
    }
  }

  &__item {
    @apply tw-rounded-full;
  }

  &__more {
    @apply tw-flex tw-items-center tw-justify-center tw-rounded-full tw-bg-gray-100 tw-font-semibold tw-text-gray-700;
  }
}
</style>
